import React, { useRef, useState } from "react";
import { useTranslation } from "../../core/translation.core";
import { useQuery } from "@tanstack/react-query";
import customerCore from "../../core/customer.core";
import { CustomerList } from "../../types/customer";
import DeleteIcon from "../../icons/delete.icon";
import LoadingOvalCenterIcon from "../../icons/loadingOvalCenter.icon";

export interface SelectedUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile?: string;
}

export default function SingleUserSearchInput({
  addUser,
}: {
  addUser: (userId: string, userDetails: { firstName: string; lastName: string; email: string }) => void;
}) {
  const [searchText, setSearch] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const { t, msg } = useTranslation();

  const {
    data: users,
    isError,
    isLoading,
  } = useQuery({
    queryKey: [searchText],
    queryFn: (context) =>
      customerCore.loadCustomers({
        search: context.queryKey[0],
      }),
    enabled: searchText !== "",
  });

  function handleUserSelect(user: SelectedUser) {
    setSelectedUser(user);
    setSearch("");
    addUser(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    });
  }

  function clearSelection() {
    setSelectedUser(null);
    addUser("", { firstName: "", lastName: "", email: "" });
  }

  return (
    <div className="space-y-4">
      {selectedUser ? (
        <div className="flex items-center justify-between p-4 border rounded-lg ">
          <div>
            <p className="font-medium uppercase">
              {selectedUser.firstName} {selectedUser.lastName}
            </p>
            <p className="text-sm">{selectedUser.email}</p>
          </div>
          <button onClick={clearSelection} className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg">
            <DeleteIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <SearchBox
            onSearch={(text) => {
              setSearch(text);
            }}
            isLoading={isLoading}
          />
          {isLoading && <LoadingOvalCenterIcon title="please wait, retrieving users" />}
          {!isError && searchText !== "" && (
            <UserList
              users={users!}
              addUser={(user) => {
                handleUserSelect(user);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

const SearchBox = ({ onSearch, isLoading }: { onSearch: (text: string) => void; isLoading: boolean }) => {
  const [searchText, setSearchText] = useState<string>("");
  const ref = useRef<HTMLInputElement>(null);
  const { t, msg } = useTranslation();
  const inputClass =
    "h-9 border w-full px-2 rounded-md mb-1 " +
    "hover:border-blue-200 hover:border-2 " +
    "focus-visible:outline-none focus-visible:border-blue-600 focus-visible:border-2 focus-visible:shadow-sm focus-visible:shadow-blue-800 " +
    "dark:bg-her-backblue " +
    "duration-1000 transition-[box-shadow]";

  function click() {
    onSearch(searchText);
    setSearchText("");

    if (ref.current) {
      ref.current.value = "";
    }
  }

  return (
    <div className="flex">
      <input
        id="user-search"
        ref={ref}
        className={inputClass}
        placeholder={t(msg.product.searchCustomer)}
        type="text"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            click();
          }
        }}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
      />

      {isLoading ? (
        <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-her-robinEggBlue mx-2"></span>
      ) : (
        <button
          id="search-user"
          // className="bg-blue-500 text-white px-4 py-1 rounded-md ml-2"
          className="bg-her-dodgerblue text-white px-4 py-1 rounded-md h-9 ml-2"
          onClick={(e) => {
            e.preventDefault();
            click();
          }}
        >
          {t(msg.general.search)}
        </button>
      )}
    </div>
  );
};

const UserList = ({ users, addUser }: { users: CustomerList; addUser: (user: SelectedUser) => void }) => {
  if (!users) return null;

  return (
    <ul id="user-search-rst" className="space-y-2 px-4">
      {users.list.map((u) => {
        return (
          <li key={u.id} className="hover:bg-gray-100 cursor-pointer">
            <button
              id="user-select"
              className="w-full text-left px-3 py-2"
              onClick={(e) => {
                e.preventDefault();
                addUser({
                  id: u.id,
                  firstName: u.firstName,
                  lastName: u.lastName,
                  email: u.email,
                });
              }}
            >
              {u.firstName} {u.lastName} - {u.email}
            </button>
          </li>
        );
      })}
    </ul>
  );
};
